// - - - - - - - - - -
// #174 — visual treatment for a LOCKED Optimizer day cell.
//
// Mirrors the existing partial-capacity RED scheme (bright outline + matching
// light inset background tint — see OptimizerGrid), but in:
//   • GREEN  when the day is FULL  (scheduled job capacity meets/exceeds the
//             installer's daily capacity → no room for another job)
//   • YELLOW when the day is UNDER capacity (room remains — including an EMPTY
//             locked day, which by Brandon's rule "does NOT meet or exceed" → yellow)
//
// Brandon's green/yellow rule (#174) keys on "does the scheduled capacity meet
// or exceed the installer capacity," which is the inverse of "is there room for
// another job." We can't reuse `partialCapacityHighlight` directly: that flag
// is FALSE for BOTH a full cell AND an empty cell (it requires ≥1 job assigned),
// so it would mis-color an empty locked day green. `hasRoomForAnotherJob` below
// derives the correct signal from the same capacityInfo the recommender uses,
// surface-aware (carpet / hard / both), without the "work today" gate.

export const LOCKED_GREEN = {
  outlineColor: "#16a34a",
  tint: "rgba(22, 163, 74, 0.10)",
};
export const LOCKED_YELLOW = {
  outlineColor: "#eab308",
  tint: "rgba(234, 179, 8, 0.12)",
};

// Job-stops cap has room? Unconfigured cap → always free (Infinity semantics,
// matching the recommenders' `parseConstraint`).
function jobStopsFree(info) {
  if (!info) return false;
  if (!info.isStopsJobsCapConfigured) return true;
  const remaining = info.remainingJobStops;
  return (remaining === undefined || remaining === null ? Infinity : remaining) > 0;
}

/**
 * Is there capacity for at least one more job on this cell? Surface-aware,
 * ignores the "≥1 job already today" gate so empty cells read as "has room".
 * @param {Object} capacityInfo - shouldHighlightPartialCapacityCell's capacityInfo
 * @returns {boolean}
 */
export function hasRoomForAnotherJob(capacityInfo) {
  if (!capacityInfo) return false;
  // Does-both installer (#175 Option B): room on EITHER surface.
  if (capacityInfo.isBothSurfaces) {
    return (
      hasRoomForAnotherJob(capacityInfo.carpetInfo) ||
      hasRoomForAnotherJob(capacityInfo.hardInfo)
    );
  }
  if (capacityInfo.isHardSurface) {
    const anyTypeHasRoom = (capacityInfo.perType || []).some(
      (pt) => pt && pt.remaining > 0
    );
    return anyTypeHasRoom && jobStopsFree(capacityInfo);
  }
  // Carpet.
  return (capacityInfo.remaining || 0) > 0 && jobStopsFree(capacityInfo);
}

/**
 * Does the cell have any scheduled jobs/repairs today? Surface-aware.
 *
 * #174 follow-up (Brandon, 2026-06-03): an **empty** locked day (e.g. a BUFFER
 * day with no job) was wrongly turning GREEN. An empty day can never "meet or
 * exceed" the installer's capacity, so it must read as under-capacity (YELLOW)
 * regardless of how the installer's caps are configured. We can't infer that
 * from remaining-room alone (an installer with no `_once` cap for a surface, or
 * the engine's zero-state, yields no "room" and would mis-color green), so we
 * gate GREEN on the day actually HAVING scheduled work.
 */
export function cellHasScheduledWork(capacityInfo) {
  if (!capacityInfo) return false;
  if (capacityInfo.isBothSurfaces) {
    return (
      cellHasScheduledWork(capacityInfo.carpetInfo) ||
      cellHasScheduledWork(capacityInfo.hardInfo)
    );
  }
  return (
    (capacityInfo.usedJobStops || 0) + (capacityInfo.usedRepairStops || 0) > 0
  );
}

// Does a SPECIFIC surface have its own scheduled work? Surface-SPECIFIC counts,
// NOT the surface-agnostic usedJobStops (which counts every job that day toward
// BOTH sub-infos — so a does-both installer's carpet jobs would otherwise make
// the idle hard surface look "worked"). Carpet → carpetJobCount; hard → any
// configured type with count > 0.
function surfaceHasOwnWork(info) {
  if (!info) return false;
  if (info.isHardSurface) {
    return (info.perType || []).some((pt) => (pt?.count || 0) > 0);
  }
  return (info.carpetJobCount || 0) > 0;
}

/**
 * Is a LOCKED day "full" (→ GREEN)? Brandon's rule: green when the day's
 * scheduled work MEETS OR EXCEEDS the installer's capacity. An empty day (no
 * work) is never full → YELLOW.
 *
 * #174 (2026-06-03) — for a does-both installer, "full" is judged against the
 * surfaces that ACTUALLY HAVE THEIR OWN WORK. Brandon's case: installer 1716.01
 * with 2 carpet jobs OVER his carpet capacity and the hard surface idle — that
 * is GREEN (over capacity), even though the idle hard surface has unused
 * capacity. The day stays YELLOW only when a *worked* surface still has room;
 * an idle surface's unused capacity does NOT keep it yellow. (Single-surface
 * installer reduces to "has work AND no room".)
 */
export function isLockedDayFull(capacityInfo) {
  if (!capacityInfo) return false;
  if (!cellHasScheduledWork(capacityInfo)) return false; // empty day → yellow
  if (capacityInfo.isBothSurfaces) {
    const aWorkedSurfaceHasRoom =
      (surfaceHasOwnWork(capacityInfo.carpetInfo) &&
        hasRoomForAnotherJob(capacityInfo.carpetInfo)) ||
      (surfaceHasOwnWork(capacityInfo.hardInfo) &&
        hasRoomForAnotherJob(capacityInfo.hardInfo));
    return !aWorkedSurfaceHasRoom;
  }
  return !hasRoomForAnotherJob(capacityInfo);
}

/**
 * The inline style for a locked cell — GREEN when the day is full, YELLOW when
 * under capacity (room remaining OR empty).
 * @param {boolean} isUnderCapacity - true → yellow, false → green
 * @returns {Object} React inline style (outline + matching light inset tint)
 */
export function getLockedCellStyle(isUnderCapacity) {
  const c = isUnderCapacity ? LOCKED_YELLOW : LOCKED_GREEN;
  return {
    outline: `2px solid ${c.outlineColor}`,
    outlineOffset: "-2px",
    boxShadow: `inset 0 0 0 100vmax ${c.tint}`,
  };
}

/** Convenience: style straight from capacityInfo (green only when full). */
export function getLockedCellStyleFromCapacity(capacityInfo) {
  return getLockedCellStyle(!isLockedDayFull(capacityInfo));
}
